import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { detectLanguage, ensureLanguage, ensureShellLanguage, applyDocumentLanguage } from './i18n'
import './styles/fonts.css'
import './index.css'
import { initTheme } from '@/lib/theme'

// Italic Newsreader is unused on the landing LCP path — load after idle so it
// does not compete with the roman face / critical JS on mobile.
const deferItalicFonts = () => {
  void import('./styles/fonts-italic.css')
}
if (typeof window !== 'undefined') {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => deferItalicFonts())
  } else {
    window.setTimeout(deferItalicFonts, 1_200)
  }
}
import {
  clearChunkReloadFlag,
  installChunkLoadRecovery,
  isChunkLoadError,
  tryRecoverFromStaleChunk,
} from '@/lib/lazy-retry'
import { installSafariResumeRecovery, markAppReady } from '@/lib/safari-resume'
import { TRPCProvider } from "@/providers/trpc"
import { initWebSentry, SentryErrorBoundary } from '@/lib/sentry'
import { CrashFallback } from '@/components/CrashFallback'
import App from './App.tsx'

initTheme();
// Before Sentry: mark recovery so beforeSend can drop the stale-chunk event.
installChunkLoadRecovery();
initWebSentry(); // no-op unless VITE_SENTRY_DSN was baked in at build time
clearChunkReloadFlag(); // successful document load → allow a future one-shot recovery
installSafariResumeRecovery(); // Safari blank-on-resume / bfcache recovery

async function boot() {
  // System/region locale + browser preferred languages (es* → Spanish).
  const lang = detectLanguage();
  applyDocumentLanguage(lang);
  await ensureShellLanguage(lang);
  void ensureLanguage(lang);
  createRoot(document.getElementById('root')!).render(
    // Keep painting the previous page while the next lazy chunk loads
    // (React startTransition). NavLink uses isPending so the tab highlight
    // still flips on the first tap — see Navbar.
    <BrowserRouter>
      <TRPCProvider>
        <SentryErrorBoundary fallback={<CrashFallback />}>
          <App />
        </SentryErrorBoundary>
      </TRPCProvider>
    </BrowserRouter>,
  );
  markAppReady();
}

void boot().catch((err) => {
  // Shell locale chunks load before React mounts — a stale hash here is an
  // unhandled rejection on / (SUTURIA-WEB-8), not a route ErrorBoundary miss.
  if (isChunkLoadError(err) && tryRecoverFromStaleChunk()) return;
  console.error(err);
});
